/* ============================================================================
   Gráfica de línea mensual.

   · Trazo de 2px, uniones redondeadas. Relleno de área al 10% sólo con una serie.
   · Marcador final ≥8px con anillo de 2px en color de superficie.
   · Rejilla y ejes: hairline sólido, un paso fuera de la superficie. Nunca punteado.
   · Crosshair que busca la X: el lector apunta a un mes, no a una línea de 2px.
   · Un solo tooltip que lista TODAS las series de ese mes.
   · Teclado: ←/→ mueven el cursor, Inicio/Fin a los extremos, Esc suelta.
   · Tabla-espejo: todo valor es alcanzable sin hover.
   ========================================================================== */

import { useCallback, useId, useMemo, useRef, useState } from 'react'
import {
  DEFAULT_INSETS,
  areaPath,
  linePath,
  linearScale,
  monthTicks,
  nearestIndex,
  niceExtent,
  unionPeriods,
  yTicks,
  type Insets,
  type Pt,
} from './primitives'
import {
  formatMonthOnly,
  formatPeriodShort,
  formatPeriodTitle,
  periodYear,
  type Period,
} from '../lib/format'
import { useSize } from '../lib/motion'
import './chart.css'

export interface ChartPoint {
  period: Period
  value: number
  /** Valor derivado (no observado). Se dibuja punteado y se advierte. */
  derived?: boolean
}

export interface ChartSeries {
  id: string
  label: string
  shortLabel: string
  /** Color de marca, siempre una variable CSS del sistema. */
  color: string
  points: ChartPoint[]
}

export interface HRule {
  value: number
  label: string
}

export interface Annotation {
  period: Period
  label: string
}

export interface LineChartProps {
  series: ChartSeries[]
  title: string
  subtitle?: string
  /** Formato del valor para tooltip, eje y tabla. */
  formatValue: (value: number) => string
  /** Descripción de la unidad, para la tabla y el aria-label. */
  unitLabel: string
  height?: number
  /** Relleno de área. Se ignora con más de una serie. */
  area?: boolean
  hRules?: HRule[]
  annotations?: Annotation[]
  /** Notifica el mes bajo el cursor (null al soltar). El encabezado lo refleja. */
  onScrub?: (period: Period | null) => void
  /** Ancla el eje Y en cero. Por omisión no, para no aplanar la señal. */
  zeroBased?: boolean
  footnote?: string
}

/**
 * Márgenes del área de trazo.
 *
 * El margen derecho depende de si van a caber las etiquetas al final de cada
 * serie: si caben hay que RESERVARLES el espacio dentro del SVG, porque si no
 * pintan fuera de su caja y ensanchan la página. Por debajo de 420 px de ancho no
 * caben y manda la leyenda, así que el margen se cierra.
 */
const END_LABEL_MIN_WIDTH = 420

function insetsFor(width: number): Insets {
  return {
    ...DEFAULT_INSETS,
    top: 20,
    bottom: 42,
    left: 54,
    right: width >= END_LABEL_MIN_WIDTH ? 62 : 20,
  }
}

export function LineChart({
  series,
  title,
  subtitle,
  formatValue,
  unitLabel,
  height = 320,
  area = false,
  hRules,
  annotations,
  onScrub,
  zeroBased = false,
  footnote,
}: LineChartProps) {
  const [boxRef, size] = useSize<HTMLDivElement>()
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '')
  const [active, setActive] = useState<number | null>(null)
  const plotRef = useRef<HTMLDivElement | null>(null)

  const periods = useMemo(
    () => unionPeriods(series.map((s) => s.points.map((p) => p.period))),
    [series],
  )

  const width = size.width || 640
  const insets = useMemo(() => insetsFor(width), [width])
  const showEndLabels = width >= END_LABEL_MIN_WIDTH
  const innerW = Math.max(1, width - insets.left - insets.right)
  const innerH = Math.max(1, height - insets.top - insets.bottom)

  const extent = useMemo(() => {
    const values = series.flatMap((s) => s.points.map((p) => p.value))
    const ruleValues = (hRules ?? []).map((r) => r.value)
    return niceExtent([...values, ...ruleValues], { zeroBased, pad: 0.14 })
  }, [series, hRules, zeroBased])

  const x = useMemo(
    () => linearScale([0, Math.max(1, periods.length - 1)], [0, innerW]),
    [periods.length, innerW],
  )
  const y = useMemo(() => linearScale([extent.min, extent.max], [innerH, 0]), [extent, innerH])

  const ticksY = useMemo(() => yTicks(extent, innerH < 180 ? 3 : 4), [extent, innerH])

  const axisMode: 'months' | 'years' = periods.length <= 18 ? 'months' : 'years'
  const maxXTicks = Math.max(2, Math.floor(innerW / 62))
  const ticksX = useMemo(
    () => monthTicks(periods, axisMode === 'months' ? maxXTicks : Math.max(2, maxXTicks)),
    [periods, maxXTicks, axisMode],
  )

  /**
   * Puntos en px por serie, más los TRAMOS continuos.
   *
   * Los tramos importan: si a una serie le falta un mes, unir los dos puntos
   * vecinos con una recta afirma que hubo una transición suave donde en realidad
   * no hay dato. Se dibuja un tramo por corrida continua de meses y el hueco
   * queda visible.
   */
  const geometry = useMemo(
    () =>
      series.map((s) => {
        const byPeriod = new Map(s.points.map((p) => [p.period, p]))
        const pts: (Pt & { point: ChartPoint })[] = []
        const segments: (Pt & { point: ChartPoint })[][] = []
        let current: (Pt & { point: ChartPoint })[] = []
        periods.forEach((period, i) => {
          const p = byPeriod.get(period)
          if (!p) {
            if (current.length > 0) {
              segments.push(current)
              current = []
            }
            return
          }
          const node = { x: x(i), y: y(p.value), point: p }
          pts.push(node)
          current.push(node)
        })
        if (current.length > 0) segments.push(current)
        return { series: s, pts, segments, byPeriod }
      }),
    [series, periods, x, y],
  )

  const singleSeries = series.length === 1
  const showArea = area && singleSeries

  const commit = useCallback(
    (index: number | null) => {
      setActive(index)
      onScrub?.(index === null ? null : (periods[index] ?? null))
    },
    [onScrub, periods],
  )

  const handlePointer = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const el = plotRef.current
      if (!el || periods.length === 0) return
      const rect = el.getBoundingClientRect()
      const px = event.clientX - rect.left - insets.left
      commit(nearestIndex(x, periods.length, px))
    },
    [commit, periods.length, x, insets.left],
  )

  const handleKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (periods.length === 0) return
      const last = periods.length - 1
      const current = active ?? last
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          commit(Math.max(0, current - 1))
          break
        case 'ArrowRight':
          event.preventDefault()
          commit(Math.min(last, current + 1))
          break
        case 'PageDown':
          event.preventDefault()
          commit(Math.max(0, current - 12))
          break
        case 'PageUp':
          event.preventDefault()
          commit(Math.min(last, current + 12))
          break
        case 'Home':
          event.preventDefault()
          commit(0)
          break
        case 'End':
          event.preventDefault()
          commit(last)
          break
        case 'Escape':
          /* Sólo se consume el Escape si hay cursor que soltar. Si no, tiene que
             seguir subiendo: dentro de una hoja modal, Escape la cierra, y
             tragárselo dejaba al usuario encerrado en la gráfica. */
          if (active !== null) {
            event.stopPropagation()
            commit(null)
          }
          break
        default:
          break
      }
    },
    [active, commit, periods.length],
  )

  const activePeriod = active === null ? null : (periods[active] ?? null)
  const activeRows = useMemo(() => {
    if (activePeriod === null) return []
    return geometry
      .map(({ series: s, byPeriod }) => {
        const p = byPeriod.get(activePeriod)
        return p ? { series: s, point: p } : null
      })
      .filter((r): r is { series: ChartSeries; point: ChartPoint } => r !== null)
  }, [geometry, activePeriod])

  /* Etiquetas directas al final de cada serie. Si colisionan, se omiten y
     manda la leyenda: apilarlas las despega de su línea y se lee como ruido. */
  const endLabels = useMemo(() => {
    if (!showEndLabels) return []
    const labels = geometry
      .map(({ series: s, pts }) => {
        const last = pts[pts.length - 1]
        return last ? { id: s.id, label: s.shortLabel, y: last.y, x: last.x } : null
      })
      .filter((l): l is { id: string; label: string; y: number; x: number } => l !== null)
    if (labels.length < 2) return labels
    const sorted = [...labels].sort((a, b) => a.y - b.y)
    for (let i = 1; i < sorted.length; i++) {
      if (Math.abs(sorted[i]!.y - sorted[i - 1]!.y) < 18) return []
    }
    return labels
  }, [geometry, showEndLabels])

  const yearBands = useMemo(() => {
    if (axisMode !== 'months') return []
    const out: { year: string; index: number }[] = []
    let lastYear = ''
    ticksX.forEach((i) => {
      const p = periods[i]
      if (!p) return
      const yr = periodYear(p)
      if (yr !== lastYear) {
        out.push({ year: yr, index: i })
        lastYear = yr
      }
    })
    return out
  }, [axisMode, ticksX, periods])

  const ariaLabel = useMemo(() => {
    const first = periods[0]
    const last = periods[periods.length - 1]
    const names = series.map((s) => s.label).join(', ')
    const lastValues = geometry
      .map(({ series: s, pts }) => {
        const p = pts[pts.length - 1]
        return p ? `${s.label}: ${formatValue(p.point.value)}` : null
      })
      .filter(Boolean)
      .join('; ')
    return `${title}. Serie mensual de ${names}${first && last ? `, de ${formatPeriodShort(first)} a ${formatPeriodShort(last)}` : ''}. Último dato — ${lastValues}. Unidad: ${unitLabel}. La tabla de datos completa está disponible bajo la gráfica.`
  }, [periods, series, geometry, title, formatValue, unitLabel])

  const hasDerived = series.some((s) => s.points.some((p) => p.derived))

  const tooltipX = active === null ? 0 : insets.left + x(active)

  /* Sin puntos no se dibuja un eje de pesos inventado: `niceExtent` devolvería
     el dominio 0–1 y la gráfica afirmaría una escala que no existe. */
  if (periods.length === 0 || series.every((s) => s.points.length === 0)) {
    return (
      <figure className="chart" aria-labelledby={`${uid}-title`}>
        <figcaption className="chart__head">
          <div className="chart__titles">
            <h3 className="t-title3" id={`${uid}-title`}>
              {title}
            </h3>
            {subtitle ? <p className="t-footnote ink-3 chart__subtitle">{subtitle}</p> : null}
          </div>
        </figcaption>
        <p className="chart__empty t-footnote ink-3">
          No hay datos publicados para este corte.
        </p>
      </figure>
    )
  }

  return (
    <figure className="chart" aria-labelledby={`${uid}-title`}>
      <figcaption className="chart__head">
        <div className="chart__titles">
          <h3 className="t-title3" id={`${uid}-title`}>
            {title}
          </h3>
          {subtitle ? <p className="t-footnote ink-3 chart__subtitle">{subtitle}</p> : null}
        </div>
        {series.length >= 2 ? (
          <ul className="chart__legend" aria-hidden="true">
            {series.map((s) => (
              <li key={s.id} className="chart__legend-item t-footnote">
                <span className="chart__key" style={{ background: s.color }} />
                {s.label}
              </li>
            ))}
          </ul>
        ) : null}
      </figcaption>

      <div
        className="chart__plot"
        ref={(node) => {
          boxRef.current = node
          plotRef.current = node
        }}
        style={{ height }}
        tabIndex={0}
        role="group"
        aria-label={ariaLabel}
        aria-describedby={`${uid}-hint`}
        onPointerDown={handlePointer}
        onPointerMove={handlePointer}
        onPointerLeave={() => commit(null)}
        onPointerCancel={() => commit(null)}
        onKeyDown={handleKey}
        /* No se crea cursor al enfocar: el `aria-label` del grupo ya anunció el
           último dato, y activarlo aquí hacía que la región viva lo repitiera
           inmediatamente. La primera flecha lo crea. */
        onBlur={() => commit(null)}
      >
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="chart__svg"
          /* El `role="group"` que envuelve ya aporta el nombre accesible. Repetir
             aquí el mismo aria-label de 300 caracteres lo hacía sonar dos veces, y
             el subárbol del SVG (rótulos de eje, anotaciones) tampoco aporta nada
             a un lector de pantalla: para eso está la tabla de datos. */
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            {series.map((s) => (
              <linearGradient
                key={s.id}
                id={`${uid}-wash-${s.id}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={s.color} stopOpacity="0.14" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0.01" />
              </linearGradient>
            ))}
          </defs>

          {/* Rejilla y rótulos del eje Y */}
          <g transform={`translate(${insets.left} ${insets.top})`}>
            {ticksY.map((t) => (
              <g key={t}>
                <line
                  x1={0}
                  x2={innerW}
                  y1={Math.round(y(t)) + 0.5}
                  y2={Math.round(y(t)) + 0.5}
                  className="chart__grid"
                />
                <text
                  x={-10}
                  y={y(t)}
                  className="chart__axis-label tabular"
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {formatValue(t)}
                </text>
              </g>
            ))}

            {/* Reglas horizontales de referencia */}
            {(hRules ?? []).map((rule) => (
              <g key={rule.label}>
                <line
                  x1={0}
                  x2={innerW}
                  y1={Math.round(y(rule.value)) + 0.5}
                  y2={Math.round(y(rule.value)) + 0.5}
                  className="chart__hrule"
                />
                <text x={4} y={y(rule.value) - 7} className="chart__rule-label">
                  {rule.label}
                </text>
              </g>
            ))}

            {/* Anotaciones verticales */}
            {(annotations ?? []).map((ann) => {
              const i = periods.indexOf(ann.period)
              if (i < 0) return null
              return (
                <g key={`${ann.period}-${ann.label}`}>
                  <line
                    x1={Math.round(x(i)) + 0.5}
                    x2={Math.round(x(i)) + 0.5}
                    y1={0}
                    y2={innerH}
                    className="chart__annotation-line"
                  />
                  <text
                    x={Math.round(x(i)) + 6}
                    y={10}
                    className="chart__annotation-label"
                  >
                    {ann.label}
                  </text>
                </g>
              )
            })}

            {/* Datos */}
            <g>
              {showArea
                ? geometry.flatMap(({ series: s, segments }) =>
                    segments
                      .filter((segment) => segment.length > 1)
                      .map((segment, index) => (
                        <path
                          key={`area-${s.id}-${index}`}
                          d={areaPath(segment, innerH)}
                          fill={`url(#${uid}-wash-${s.id})`}
                        />
                      )),
                  )
                : null}

              {geometry.flatMap(({ series: s, segments }) =>
                segments.map((segment, index) =>
                  segment.length > 1 ? (
                    <path
                      key={`line-${s.id}-${index}`}
                      d={linePath(segment)}
                      className="chart__line"
                      stroke={s.color}
                    />
                  ) : (
                    <circle
                      key={`dot-${s.id}-${index}`}
                      cx={segment[0]!.x}
                      cy={segment[0]!.y}
                      r={2.5}
                      fill={s.color}
                    />
                  ),
                ),
              )}

              {/* Segmentos derivados, punteados */}
              {/* Recorre `segments`, no `pts`: sobre la lista plana el trazo
                  punteado volvía a cruzar los huecos que el sólido ya no cruza. */}
              {geometry.map(({ series: s, segments }) => {
                const segs: Pt[][] = []
                for (const segment of segments) {
                  let cur: Pt[] = []
                  segment.forEach((p, i) => {
                    if (p.point.derived) {
                      if (cur.length === 0 && i > 0) cur.push(segment[i - 1]!)
                      cur.push(p)
                    } else if (cur.length > 0) {
                      cur.push(p)
                      segs.push(cur)
                      cur = []
                    }
                  })
                  if (cur.length > 1) segs.push(cur)
                }
                return segs.map((seg, i) => (
                  <path
                    key={`derived-${s.id}-${i}`}
                    d={linePath(seg)}
                    className="chart__line chart__line--derived"
                    stroke={s.color}
                  />
                ))
              })}
            </g>

            {/* Base del eje X */}
            <line
              x1={0}
              x2={innerW}
              y1={innerH + 0.5}
              y2={innerH + 0.5}
              className="chart__axis"
            />

            {/* Marcador y etiqueta del último punto */}
            {geometry.map(({ series: s, pts }) => {
              const last = pts[pts.length - 1]
              if (!last) return null
              return (
                <circle
                  key={`end-${s.id}`}
                  cx={last.x}
                  cy={last.y}
                  r={4.5}
                  fill={s.color}
                  className="chart__end-dot"
                />
              )
            })}

            {endLabels.map((l) => (
              <text
                key={`endlabel-${l.id}`}
                x={l.x + 9}
                y={l.y}
                className="chart__end-label"
                dominantBaseline="middle"
              >
                {l.label}
              </text>
            ))}

            {/* Crosshair */}
            {active !== null ? (
              <g className="chart__cursor">
                <line
                  x1={Math.round(x(active)) + 0.5}
                  x2={Math.round(x(active)) + 0.5}
                  y1={-6}
                  y2={innerH}
                  className="chart__crosshair"
                />
                {activeRows.map(({ series: s, point }) => (
                  <circle
                    key={`cursor-${s.id}`}
                    cx={x(active)}
                    cy={y(point.value)}
                    r={5}
                    fill={s.color}
                    className="chart__cursor-dot"
                  />
                ))}
              </g>
            ) : null}

            {/* Rótulos del eje X */}
            {ticksX.map((i) => {
              const p = periods[i]
              if (!p) return null
              const label = axisMode === 'months' ? formatMonthOnly(p) : periodYear(p)
              const anchor = i === 0 ? 'start' : i === periods.length - 1 ? 'end' : 'middle'
              const dx = i === 0 ? -3 : i === periods.length - 1 ? 3 : 0
              return (
                <text
                  key={`tickx-${p}`}
                  x={x(i) + dx}
                  y={innerH + 17}
                  className="chart__axis-label"
                  textAnchor={anchor}
                >
                  {label}
                </text>
              )
            })}

            {yearBands.map((band) => (
              <text
                key={`year-${band.year}`}
                x={x(band.index)}
                y={innerH + 33}
                className="chart__year-label"
                textAnchor={band.index === 0 ? 'start' : 'middle'}
              >
                {band.year}
              </text>
            ))}
          </g>
        </svg>

        {activePeriod && activeRows.length > 0 ? (
          <div
            className="chart__tooltip"
            style={{
              left: `${tooltipX}px`,
              transform: `translateX(${tooltipX > width * 0.62 ? 'calc(-100% - 12px)' : '12px'})`,
            }}
          >
            <p className="chart__tooltip-period t-micro ink-3">{formatPeriodTitle(activePeriod)}</p>
            <ul>
              {activeRows.map(({ series: s, point }) => (
                <li key={s.id}>
                  <span className="chart__tooltip-key" style={{ background: s.color }} />
                  <span className="chart__tooltip-value tabular">{formatValue(point.value)}</span>
                  <span className="chart__tooltip-label t-caption ink-2">
                    {s.label}
                    {point.derived ? ' · estimado' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <p className="sr-only" aria-live="polite">
        {activePeriod
          ? `${formatPeriodTitle(activePeriod)}: ${activeRows
              .map(({ series: s, point }) => `${s.label} ${formatValue(point.value)}`)
              .join(', ')}`
          : ''}
      </p>

      <p className="sr-only" id={`${uid}-hint`}>
        Gráfica interactiva. Use las flechas izquierda y derecha para recorrer los meses,
        Inicio y Fin para ir a los extremos, Escape para soltar el cursor.
      </p>

      <div className="chart__foot">
        {footnote ? <p className="t-caption ink-3 chart__footnote">{footnote}</p> : null}
        {hasDerived ? (
          <p className="t-caption ink-3 chart__footnote">
            El tramo punteado es estimado, no observado.
          </p>
        ) : null}
        <details className="chart__table">
          <summary className="t-footnote">Ver tabla de datos</summary>
          {/* Enfocable a propósito: es un contenedor con scroll sin nada
              enfocable dentro, y sin `tabIndex` no hay forma de desplazarlo con
              teclado. Esta tabla es el canal de respaldo declarado de la gráfica,
              así que tiene que ser operable sin ratón. */}
          <div
            className="chart__table-scroll"
            tabIndex={0}
            role="region"
            aria-label={`Tabla de datos de ${title}`}
          >
            <table>
              <caption className="sr-only">
                {title}. {unitLabel}.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Mes</th>
                  {series.map((s) => (
                    <th scope="col" key={s.id}>
                      {s.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...periods].reverse().map((p) => (
                  <tr key={p}>
                    <th scope="row">{formatPeriodShort(p)}</th>
                    {geometry.map(({ series: s, byPeriod }) => {
                      const pt = byPeriod.get(p)
                      return (
                        <td key={s.id} className="tabular">
                          {pt ? formatValue(pt.value) : '—'}
                          {pt?.derived ? ' *' : ''}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </figure>
  )
}
